import React, {useState, useEffect} from "react";
import {useParams} from "react-router-dom";
import {useDispatch, useSelector} from 'react-redux';
import {Col, Row, Alert, Card, CardBody, CardHeader} from "reactstrap";
import {Button as PrimeReactButton} from "primereact/button";
import {MultiSelect} from "primereact/multiselect";
import {RadioButton} from "primereact/radiobutton";

// services
import {BADGE_TYPES, PROPOSAL_STATUSES, QCE_FREQUENCY_TYPES, PROPOSAL_VIEW} from "../../services/appConstants";
import BadgeWithIcon from "./BadgeWithIcon";
import * as newProposalActions from "../../redux/actions/newProposalActions";

const OptionalServices = ({view}) => {
    let {id} = useParams();
    let dispatch = useDispatch();

    const {proposal, lines, expiringLines, underContractLines} = useSelector(state => state.newProposalReducer);

    const proposalStatusesForOptionalServices = [PROPOSAL_STATUSES.SENT.value, PROPOSAL_STATUSES.VIEWED.value, PROPOSAL_STATUSES.MODIFIED.value];

    const [agreedToExpediteFee, setAgreedToExpediteFee] = useState(true);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [aocTracking, setAocTracking] = useState(true);
    const [qce, setQce] = useState(true);
    const [qceFrequency, setQceFrequency] = useState(QCE_FREQUENCY_TYPES.SEMI_ANNUAL.value);

    const isFeatureDisabled = view === PROPOSAL_VIEW.ALL;
    const [optionalServicesEnabled, setOptionalServicesEnabled] = useState(false);

    const infoMessage =
      view === PROPOSAL_VIEW.CURRENT ? (
        <>
          You can add any Optional Services to these inspections but removing already contracted services is not possible. If you would like further help,
          please contact our support team at{" "}
          <a href="mailto:metroinspections@vda.com">metroinspections@vda.com</a>
        </>
      ) :("") ;

    useEffect(() => {
        if (qce) {
            setQceFrequency(QCE_FREQUENCY_TYPES.SEMI_ANNUAL.value);
        }
    }, [qce]);

    useEffect(() => {
        const sources = {
            [PROPOSAL_VIEW.ALL]: lines,
            [PROPOSAL_VIEW.CURRENT]: underContractLines,
            [PROPOSAL_VIEW.EXPIRING]: expiringLines,
        };

        const source = sources[view] || [];
        const seen = new Set();

        const locations = source.reduce((acc, line) => {
            if (!seen.has(line.buildingAddress)) {
                seen.add(line.buildingAddress);
                acc.push({label: line.buildingAddress, value: line.buildingId});
            }

            return acc;
        }, []);

        setAvailableLocations(locations);
        if ([PROPOSAL_VIEW.CURRENT, PROPOSAL_VIEW.EXPIRING].includes(view)) {
            setSelectedLocations([]);
        }
    }, [view, lines, underContractLines, expiringLines]);


    useEffect(() => {
        setOptionalServicesEnabled(proposalStatusesForOptionalServices.includes(proposal?.statusId));
    }, [proposal?.statusId]);

    const handleUpdateLocations = async () => {
        const request = {
            buildingIds: selectedLocations,
            proposalId: id,
            agreedToExpeditingFee: agreedToExpediteFee,
            aocTracking: aocTracking,
            qce: qce,
            qceFrequency: qce ? qceFrequency : 0,
            areUnderContractBuildings : view === PROPOSAL_VIEW.CURRENT
        };

        dispatch(newProposalActions.updateOptionalServices(request));
    };

    const clearSelection = () => {
        setSelectedLocations([]);
        setAgreedToExpediteFee(false);
        setAocTracking(false);
        setQce(false);
        setQceFrequency(QCE_FREQUENCY_TYPES.ANNUAL.value);
    };

    const formatQceFrequencies = () => {
        const isDisabled = !qce;

        return (
            <div style={{
                opacity: isDisabled ? 0.5 : 1,
                pointerEvents: isDisabled ? "none" : "auto",
                textAlign: "center",
                paddingBottom: "1rem"
            }}>
                <b>Elevate your QCE experience</b>
                <div style={{
                    marginTop: "0.5rem",
                    display: "flex",
                    gap: "1.5rem",
                    alignItems: "center",
                    justifyContent: "center"
                }}>
                    <div style={{display: "flex", alignItems: "center"}}>
                        <RadioButton
                            inputId="annual"
                            name="qceAnnual"
                            value={QCE_FREQUENCY_TYPES.ANNUAL.value}
                            checked={qceFrequency === QCE_FREQUENCY_TYPES.ANNUAL.value}
                            onChange={() => setQceFrequency(QCE_FREQUENCY_TYPES.ANNUAL.value)}
                            disabled={isDisabled}
                        />
                        <label htmlFor="qceAnnual" className="ml-2">
                            {QCE_FREQUENCY_TYPES.ANNUAL.label}
                        </label>
                    </div>
                    <div style={{position: "relative", display: "flex", alignItems: "center"}}>
                        <RadioButton
                            inputId="semiAnnual"
                            name="qceSemiAnnual"
                            value={QCE_FREQUENCY_TYPES.SEMI_ANNUAL.value}
                            checked={qceFrequency === QCE_FREQUENCY_TYPES.SEMI_ANNUAL.value}
                            onChange={() => setQceFrequency(QCE_FREQUENCY_TYPES.SEMI_ANNUAL.value)}
                            disabled={isDisabled}
                        />
                        <label htmlFor="qceSemiAnnual" className="ml-2">
                            {QCE_FREQUENCY_TYPES.SEMI_ANNUAL.label}
                        </label>
                        <div style={{position: "absolute", top: "1.5rem", left: "1.4rem"}}>
                            <BadgeWithIcon
                                type={BADGE_TYPES.SUCCESS}
                                style={{maxWidth: "min-content"}}
                                text="Recommended"
                            />
                        </div>
                    </div>
                    <div style={{display: "flex", alignItems: "center"}}>
                        <RadioButton
                            inputId="quarterly"
                            name="qceQuarterly"
                            value={QCE_FREQUENCY_TYPES.QUARTERLY.value}
                            checked={qceFrequency === QCE_FREQUENCY_TYPES.QUARTERLY.value}
                            onChange={() => setQceFrequency(QCE_FREQUENCY_TYPES.QUARTERLY.value)}
                            disabled={isDisabled}
                        />
                        <label htmlFor="qceQuarterly" className="ml-2">
                            {QCE_FREQUENCY_TYPES.QUARTERLY.label}
                        </label>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={{
            opacity: (optionalServicesEnabled && !isFeatureDisabled) ? 1 : 0.5,
            pointerEvents: (optionalServicesEnabled && !isFeatureDisabled) ? "auto" : "none",
        }}>
            <Row className="align-items-center mb-4">
                <Col xs="12">
                    <Card className="mb-3">
                        <CardHeader className="bg-light">
                            <h4>Optional services</h4>
                        </CardHeader>
                        <CardBody>
                            <div style={{
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                padding: "1rem",
                                marginBottom: "1rem",
                                background: "#fff",
                                //position: "relative",
                            }}>
                                <Row>
                                    <Col style={{display: "flex", placeItems: "center", gap: "2rem"}}>                                        
                                        <span>
                                            <b>Locations:</b>
                                        </span>
                                        <MultiSelect
                                            value={selectedLocations}
                                            options={availableLocations}
                                            onChange={(e) => setSelectedLocations(e.value)}
                                            placeholder="Select Locations"
                                            selectAllLabel="Select All"
                                            display="chip"
                                            style={{
                                                width: "400px",
                                                color: "green",
                                                fontStyle: "italic",
                                                marginBottom: "0.5rem",
                                                marginLeft: "0.5rem",
                                                minWidth: "220px",
                                            }}
                                            disabled={!optionalServicesEnabled}
                                        />
                                    </Col>
                                    <Col style={{
                                        display: "flex",
                                        gap: "0.5rem",
                                        justifyContent: "right",
                                        height: "min-content"
                                    }}>
                                        <PrimeReactButton
                                            label="Update the selected locations"
                                            icon="pi pi-check"
                                            className="p-button-success p-button-sm"
                                            onClick={handleUpdateLocations}
                                            disabled={selectedLocations.length === 0}
                                            style={{
                                                minWidth: "70px",
                                                fontSize: "0.85rem",
                                                padding: "0.25rem 0.75rem",
                                            }}
                                        />
                                        <PrimeReactButton
                                            label="Clear Selection"
                                            icon="pi pi-times"
                                            className="p-button-secondary p-button-sm"
                                            onClick={clearSelection}
                                            style={{
                                                minWidth: "70px",
                                                fontSize: "0.85rem",
                                                padding: "0.25rem 0.75rem",
                                            }}
                                        />
                                    </Col>
                                </Row>
                                <Row style={{marginTop: "2rem"}}>
                                    <Col md={3} lg={3}>
                                        <b>Expedite Filing Fee Agreement</b>
                                        <div style={{marginTop: "0.5rem"}}>
                                            <RadioButton
                                                inputId="expediteYes"
                                                name="expediteFiling"
                                                value={true}
                                                checked={agreedToExpediteFee === true}
                                                onChange={() => setAgreedToExpediteFee(true)}
                                            />
                                            <label htmlFor="expediteYes" className="ml-2 mr-3">
                                                Yes
                                            </label>
                                            <RadioButton
                                                inputId="expediteNo"
                                                name="expediteFiling"
                                                value={false}
                                                checked={agreedToExpediteFee === false}
                                                onChange={() => setAgreedToExpediteFee(false)}
                                            />
                                            <label htmlFor="expediteNo" className="ml-2">
                                                No
                                            </label>
                                        </div>
                                    </Col>
                                    <Col md={3} lg={3}>
                                        <b>AOC Tracking</b>
                                        <div style={{marginTop: "0.5rem"}}>
                                            <RadioButton
                                                inputId="aocYes"
                                                name="aocTracking"
                                                value={true}
                                                checked={aocTracking === true}
                                                onChange={() => setAocTracking(true)}
                                            />
                                            <label htmlFor="aocYes" className="ml-2 mr-3">
                                                Yes
                                            </label>
                                            <RadioButton
                                                inputId="aocNo"
                                                name="aocTracking"
                                                value={false}
                                                checked={aocTracking === false}
                                                onChange={() => setAocTracking(false)}
                                            />
                                            <label htmlFor="aocNo" className="ml-2">
                                                No
                                            </label>
                                        </div>
                                    </Col>
                                    <Col md={2} lg={2}>
                                        <b>QCE</b>
                                        <div style={{marginTop: "0.5rem"}}>
                                            <RadioButton
                                                inputId="qceYes"
                                                name="qce"
                                                value={true}
                                                checked={qce}
                                                onChange={() => setQce(true)}/>
                                            <label htmlFor="qceYes" className="ml-2 mr-3">
                                                Yes
                                            </label>
                                            <RadioButton
                                                inputId="qceNo"
                                                name="qce"
                                                value={false}
                                                checked={!qce}
                                                onChange={() => setQce(false)}/>
                                            <label htmlFor="qceNo" className="ml-2">
                                                No
                                            </label>
                                        </div>
                                    </Col>
                                    <Col md={4} lg={4}>
                                        {formatQceFrequencies()}
                                    </Col>
                                </Row>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {[PROPOSAL_VIEW.CURRENT, PROPOSAL_VIEW.ALL].includes(view) && (
                <Row className="mb-2">
                    <Col xs="12">
                        <Alert color="info" className="text-center">
                            {infoMessage}
                        </Alert>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default OptionalServices;
